import { toLegacyDetail, v1Get, v1Post } from '@/services/v1Api';

/**
 * Fetch all equipment lists for a specific project from the warehouse API
 * @param {number|string} projectId - The project ID
 * @returns {Promise<Object>} Object containing project info, equipment lists, and overall summary
 */
export const getProjectEquipmentLists = async (projectId) => {
    try {
        const { data } = await v1Get(`/api/v1/warehouse/projects/${projectId}/equipment-lists`);
        return data;
    } catch (error) {
        console.error('Error fetching project equipment lists:', error);
        throw error;
    }
};

/**
 * Fetch specific equipment list data for warehouse operations
 * @param {number|string} projectId - The project ID
 * @param {number|string} equipmentListId - The equipment list ID
 * @returns {Promise<Object>} Object containing equipment list data
 */
export const getEquipmentListWarehouseData = async (projectId, equipmentListId) => {
    try {
        const { data } = await v1Get(
            `/api/v1/warehouse/projects/${projectId}/equipment-lists/${equipmentListId}/warehouse`
        );
        return data;
    } catch (error) {
        console.error('Error fetching equipment list warehouse data:', error);
        throw error;
    }
};

/**
 * Fetch all cases for an equipment list in a project
 * @param {number|string} projectId - The project ID
 * @param {number|string} equipmentListId - The equipment list ID
 * @returns {Promise<Array>} Array of cases with their contents
 */
export const getProjectCases = async (projectId, equipmentListId) => {
    try {
        const { data } = await v1Get(
            `/api/v1/warehouse/projects/${projectId}/equipment-lists/${equipmentListId}/cases`
        );
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching cases:', error);
        throw error;
    }
};

/**
 * Set the current open case for an equipment list on the backend
 * @param {number|string} projectId - The project ID
 * @param {number|string} equipmentListId - The equipment list ID
 * @param {number|string} caseId - The case ID (local_asset_id)
 * @returns {Promise<Object>} Response object
 */
export const setCurrentCase = async (projectId, equipmentListId, caseId) => {
    try {
        const requestBody = { open_case_id: caseId };

        const { data } = await v1Post(
            `/api/v1/warehouse/projects/${projectId}/equipment-lists/${equipmentListId}/cases/current`,
            requestBody
        );
        return toLegacyDetail(data);
    } catch (error) {
        console.error('Error setting current case:', error);
        throw error;
    }
};

/**
 * Fetch the current open case for an equipment list from the backend
 * This syncs the frontend with the backend state of which case is currently open
 * @param {number|string} projectId - The project ID
 * @param {number|string} equipmentListId - The equipment list ID
 * @returns {Promise<Object|null>} The current case object or null if no case is open
 */
export const getCurrentCase = async (projectId, equipmentListId) => {
    try {
        const { data } = await v1Get(
            `/api/v1/warehouse/projects/${projectId}/equipment-lists/${equipmentListId}/cases/current`
        );

        const caseData = data?.case ?? data ?? null;

        if (caseData && caseData.id) {
            const assetNumber =
                caseData.asset_number ||
                caseData.case_number ||
                null;

            if (assetNumber) {
                setOpenCaseId(projectId, equipmentListId, caseData.id, assetNumber);
            }

            return caseData;
        }

        return null;
    } catch (error) {
        if (error.response?.status === 404) {
            return null;
        }
        console.error('Error fetching current case:', error);
        return null;
    }
};

/**
 * Manually close a case
 * @param {number|string} projectId - The project ID
 * @param {number|string} caseId - The case ID (local_asset_id)
 * @returns {Promise<Object>} Response object
 */
export const closeCase = async (projectId, caseId) => {
    try {
        const { data } = await v1Post(
            `/api/v1/warehouse/projects/${projectId}/cases/close`,
            { case_id: caseId }
        );

        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('open_case_id_') && localStorage.getItem(key) === String(caseId)) {
                localStorage.removeItem(key);
            }
        });

        return toLegacyDetail(data);
    } catch (error) {
        console.error('Error closing case:', error);
        throw error;
    }
};

/**
 * Store the open case ID and asset number in localStorage
 * This is called when scan endpoint returns case_opened or case_switched
 * @param {number|string} projectId - The project ID
 * @param {number|string} equipmentListId - The equipment list ID
 * @param {number|string} caseId - The case ID (local_asset_id)
 * @param {string} assetNumber - Optional asset number/barcode for display
 */
export const setOpenCaseId = (projectId, equipmentListId, caseId, assetNumber = null) => {
    const key = `open_case_id_${projectId}_${equipmentListId}`;
    const assetNumberKey = `open_case_asset_number_${projectId}_${equipmentListId}`;
    if (caseId) {
        localStorage.setItem(key, String(caseId));
        if (assetNumber) {
            localStorage.setItem(assetNumberKey, String(assetNumber));
        }
    } else {
        localStorage.removeItem(key);
        localStorage.removeItem(assetNumberKey);
    }
};

/**
 * Get the stored asset number for the open case
 * @param {number|string} projectId - The project ID
 * @param {number|string} equipmentListId - The equipment list ID
 * @returns {string|null} The stored asset number or null
 */
export const getOpenCaseAssetNumber = (projectId, equipmentListId) => {
    const key = `open_case_asset_number_${projectId}_${equipmentListId}`;
    return localStorage.getItem(key);
};

/**
 * Get the open case ID from localStorage
 * @param {number|string} projectId - The project ID
 * @param {number|string} equipmentListId - The equipment list ID
 * @returns {number|null} The open case ID or null
 */
export const getOpenCaseId = (projectId, equipmentListId) => {
    const key = `open_case_id_${projectId}_${equipmentListId}`;
    const value = localStorage.getItem(key);
    return value ? parseInt(value) : null;
};

/**
 * Clear the open case ID and asset number from localStorage
 * @param {number|string} projectId - The project ID
 * @param {number|string} equipmentListId - The equipment list ID
 */
export const clearOpenCaseId = (projectId, equipmentListId) => {
    const key = `open_case_id_${projectId}_${equipmentListId}`;
    const assetNumberKey = `open_case_asset_number_${projectId}_${equipmentListId}`;
    localStorage.removeItem(key);
    localStorage.removeItem(assetNumberKey);
};
